/**
 * Some account settings require authorization or new monday scopes to work.
 * The backend refuses to turn such a setting on with 409 `REAUTHORIZATION_REQUIRED`.
 * 
 * For missing scopes (reason: "missing_scope"): Monday automatically pushes new app versions,
 * but new scopes are only granted after an ACCOUNT ADMIN approves the update in monday
 * (Administration > Apps > Same Item Multiple Boards > Update). Re-running the OAuth flow
 * does NOT grant missing scopes, so the frontend guides users to the admin approval flow.
 * 
 * For auth failures (reason: "token_invalid", expired/revoked tokens): This module triggers
 * the re-authorization round trip:
 *   toggle ON → 409 → remember the setting → /oauth/authorize?return_to=settings (in the iframe)
 *   → monday consent → callback → /settings?authorized=true → the Settings page re-applies the
 *   remembered setting (and shows an error instead of looping when auth still fails).
 */
export const REAUTHORIZATION_REQUIRED_CODE = "REAUTHORIZATION_REQUIRED";

/** localStorage key holding the setting to re-apply once the callback lands on /settings. */
export const PENDING_SETTING_AFTER_AUTH_KEY = "pendingSettingAfterAuth";

export interface ReauthorizationRefusal {
  code: typeof REAUTHORIZATION_REQUIRED_CODE;
  reason?: "missing_scope" | "token_invalid";
  missingScopes?: string[];
  settingKey?: string;
  error_message?: string;
  message?: string;
  requires_admin_approval?: boolean;
}

/** The refusal body when `error` is the backend's 409 REAUTHORIZATION_REQUIRED, else null. */
export const getReauthorizationRefusal = (error: unknown): ReauthorizationRefusal | null => {
  const response = (error as { response?: { status?: number; data?: any } } | undefined)?.response;
  if (response?.status === 409 && response?.data?.code === REAUTHORIZATION_REQUIRED_CODE) {
    return response.data as ReauthorizationRefusal;
  }
  return null;
};

/**
 * The backend's PKCE initiator, told to bring the user back to a named page after the callback.
 * `source=iframe` because Settings runs inside monday's app-feature modal and the OAuth screen
 * can load in place there, exactly like the onboarding flow.
 */
export const buildReauthorizeUrl = ({
  appVersionId,
  returnTo,
}: {
  appVersionId?: string | number | null;
  returnTo: "settings";
}): string => {
  const params = new URLSearchParams({ source: "iframe", return_to: returnTo });
  if (appVersionId) params.set("app_version_id", String(appVersionId));
  return `${process.env.REACT_APP_URI}/oauth/authorize?${params.toString()}`;
};

// localStorage can throw in private/blocked contexts; a failed remember just means the user
// toggles once more after re-authorizing, so never let it break the redirect.
export const rememberPendingSetting = (settingKey: string): void => {
  try {
    localStorage.setItem(PENDING_SETTING_AFTER_AUTH_KEY, settingKey);
  } catch {
    /* ignore */
  }
};

export const takePendingSetting = (): string | null => {
  try {
    const value = localStorage.getItem(PENDING_SETTING_AFTER_AUTH_KEY);
    localStorage.removeItem(PENDING_SETTING_AFTER_AUTH_KEY);
    return value;
  } catch {
    return null;
  }
};
